import Form from 'react-bootstrap/Form';
import { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/esm/Button';
import { DataTableColumn } from '../widget/DataTable';
import DataTable from '../widget/DataTable';
import Accordion from 'react-bootstrap/Accordion';
import { renderMarkdown, RichTextEditor } from '../widget/RichText';
import { getData } from '../../utils/GetData';
import { EmailOptOut } from '../../models/EmailOptOut';
import { sharedSearchCache } from '../../../api/SearchCache';
import Modal from 'react-bootstrap/esm/Modal';
import MailEndpoint from '../../../api/endpoint/MailEndpoint';
import Spinner from 'react-bootstrap/esm/Spinner';

export const EmailTool = () => {
    const [emailBody, setEmailBody] = useState('');
    const [subject, setSubject] = useState('');

    const [emailHtmlSnapshot, setEmailHtmlSnapshot] = useState('');
    const [subjectSnapshot, setSubjectSnapshot] = useState('');
    const [rejected, setRejected] = useState<string[]>([]);

    const [emails, setEmails] = useState<EmailOptOut[] | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ column: string | null; direction: "asc" | "desc" | "none" }>({ column: null, direction: "asc" });
    const [pageSize, setPageSize] = useState(5);
    const [searchError, setSearchError] = useState<string | null>(null);
    
    const [showWarning, setShowWarning] = useState(false);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showResend, setShowResend] = useState(false);

    const [needsReload, setNeedsReload] = useState(false);

    const cols: DataTableColumn<EmailOptOut>[] = [
        {column: "email", label: "Email", sortable: true, headerClassName: "sortable-header"},
        {column: "opted_out", label:"Opted Out", sortable: true, headerClassName: "sortable-header",
            accessor: (row: EmailOptOut) => {
                return(
                    <>
                        {row.opted_out &&
                            <>Yes</>
                        }
                        {!row.opted_out &&
                            <>No</>
                        }
                    </>
                )
            }}
    ];

    const getEmails = async () => {
        const result = await getData<EmailOptOut>(
            'api/opt-out/search',
            page,
            pageSize,
            {
                column: sort.column,
                direction: sort.direction
            }
        );

        if(typeof result === 'object') {
            const emailsResult: EmailOptOut[] = result.data.map((row: any) => ({
                email_opt_out_id: Number(row.email_opt_out_id),
                email: row.email,
                opted_out: row.opted_out
            }));
            setEmails(emailsResult);
            setTotal(result.total);
            setSearchError(null);
        } else {
            setSearchError(result);
        }
    };

    useEffect(() => {
        getEmails();
    }, [page, sort, pageSize]);

        useEffect(() => {
            initiateReload();
        }, [page, sort, pageSize]);
    
        useEffect(() => {
            if(needsReload) {
                getEmails();
                setNeedsReload(false);
            }
        }, [needsReload]);
        
        const initiateReload = useCallback(() => {
            sharedSearchCache.clear();
            setTimeout(() => {
                setNeedsReload(true);
            }, 1);
        }, [setNeedsReload]);

    const handleSort = (col: DataTableColumn<EmailOptOut>) => {
        if (!col.sortable) return;
        let direction: "asc" | "desc" | "none" = "asc";
        if (sort.column === col.column) {
            if (sort.direction === "asc") {
                direction = "desc";
            } else if (sort.direction === "desc") {
                direction = "none";
            } else {
                direction = "asc";
            }
        }
        const newSort = { column: direction === "none" ? null : col.column, direction };
        setSort(newSort);
        setPage(1);
        initiateReload();
    };

    const handleSend = async () => {
        try {
            setShowResend(false);
            setIsLoading(true);
            const html = renderMarkdown(emailBody);
            const mailResult = await MailEndpoint.SendByOptedIn({
                subject, 
                html
            });
            setIsLoading(false);

            if(mailResult.errors && mailResult.errors.length > 0) {
                setMessage('Error: ' + mailResult.errors.join(', '));
                return;
            }

            if(mailResult.data.rejected && mailResult.data.rejected.length > 0) {
                setMessage('Failure sending to the following emails: ' + mailResult.data.rejected.join(', '));
                setEmailHtmlSnapshot(html);
                setSubjectSnapshot(subject);
                setRejected(mailResult.data.rejected);
                setShowResend(true);
                return;
            }

            setMessage('Successfully sent emails.');
            setEmailBody('');
            setSubject('');
        } catch(err) {
            setIsLoading(false);
            setMessage('Error: ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    const handleResend = async () => {
        try {
            setShowResend(false);
            setIsLoading(true);
            const mailResult = await MailEndpoint.SendByRecipientList({
                to: rejected,
                subject: subjectSnapshot, 
                html: emailHtmlSnapshot
            });
            setIsLoading(false);

            if(mailResult.errors && mailResult.errors.length > 0) {
                setShowResend(true);
                setMessage('Error: ' + mailResult.errors.join(', '));
                return;
            }

            if(mailResult.data.rejected && mailResult.data.rejected.length > 0) {
                setMessage('Failure sending to the following emails: ' + mailResult.data.rejected.join(', '));
                setShowResend(true);
                setRejected(mailResult.data.rejected);
                return;
            }

            setMessage('Successfully sent emails.');
            setEmailBody('');
            setSubject('');
        } catch(err) {
            setIsLoading(false);
            setShowResend(true);
            setMessage('Error: ' + (err instanceof Error ? err.message : String(err)));
        }
    }

    const handleSendClick = () => {
        if(subject === '' || checkIfEmptyHtml(subject) || emailBody === '' || checkIfEmptyHtml(emailBody))
            setShowWarning(true);
        else
            handleSend();
    }

    const checkIfEmptyHtml = (str: string): boolean => {
        const re = /<[\s\S]+><\/[\s\S]+>/;
        return re.test(str);
    }
    
    return (
        <div>
            <Modal show={showWarning} onHide={() => setShowWarning(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Are you sure you want to send?</Modal.Title>
                </Modal.Header>
                    
                <Modal.Body>
                    {(subject === "" || checkIfEmptyHtml(subject)) &&
                        <div className="text-danger">Email is missing a subject
                        { emailBody === "" || checkIfEmptyHtml(emailBody) &&
                            <> and a body</>
                        }
                        .</div>
                    }
                    {(emailBody === "" || checkIfEmptyHtml(emailBody)) && (subject !== "" && !checkIfEmptyHtml(subject))&&
                        <div className="text-danger">Email is missing a body.</div>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowWarning(false)}>Cancel</Button>
                    <Button variant="primary" className="text-light" onClick={handleSend}>Send</Button>
                </Modal.Footer>
            </Modal>
            <h1 className="text-light">Email Tool</h1>
            <hr className="border"/>

            <div style={{padding:"1em", backgroundColor:"white", borderRadius:"0.5em"}}>
                <Form.Control style={{marginBottom: "1em", borderColor: '#ccc'}} type="text" placeholder="Subject" value={subject} onChange={(e:any) => setSubject(e.target.value)}/>
                <RichTextEditor
                    id="email-body"
                    ariaLabel="email body"
                    value={emailBody} 
                    onChange={setEmailBody} 
                    placeholder="Email body goes here."
                />
                <div className="d-flex gap-4">
                    <Button className="text-light" onClick={handleSendClick}>Send</Button>
                    {isLoading &&
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    }
                    {!isLoading &&
                        <div className={message.includes('Success') ? 'text-success' : 'text-danger'}>
                            {message}
                            {showResend &&
                                <>
                                    <br />
                                    Click 
                                        <button onClick={handleResend} style={{backgroundColor: 'transparent', borderColor: 'transparent', borderBottom: '1px solid', padding: '2px'}}>
                                            here
                                        </button> 
                                    to attempt re-send the failed emails.
                                </>
                            }
                        </div>
                    }
                </div>
                

                <Accordion style={{paddingTop: "1em"}}>
                    <Accordion.Item eventKey='0'>
                        <Accordion.Header>View User Emails</Accordion.Header>
                        <Accordion.Body>
                            {searchError &&
                                <div>
                                    <strong>Search error:</strong> {searchError}
                                </div>
                            }
                            {emails &&
                                <DataTable 
                                    tableClassName="table-stack-xs"
                                    data={emails} 
                                    columns={cols} 
                                    id={'user-emails-table'} 
                                    page={page} 
                                    pageSize={pageSize} 
                                    totalRecords={total}
                                    onPageChange={setPage}
                                    handlePageSizeChange={(newPageSize: number) => {
                                        setPageSize(newPageSize);
                                        setPage(1);  // Reset to first page when changing items per page
                                    }}
                                    sort={sort}
                                    onSort={handleSort}
                                />
                            }
                            {!emails &&
                                <div>No emails available.</div>
                            }
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
                
            </div>
        </div>
    )
}